import { getInfo as getTinosInfo, loadFont as tinos } from "@remotion/google-fonts/Tinos";
import { getInfo as getRobotoInfo, loadFont as roboto } from "@remotion/google-fonts/Roboto";
import { getInfo as getLatoInfo, loadFont as lato } from "@remotion/google-fonts/Lato";
import { getInfo as getMontserratInfo, loadFont as montserrat } from "@remotion/google-fonts/Montserrat";
import { getInfo as getOpenSansInfo, loadFont as openSans } from "@remotion/google-fonts/OpenSans";
import { getInfo as getPlayfairDisplayInfo, loadFont as playfairDisplay } from "@remotion/google-fonts/PlayfairDisplay";
import { getInfo as getPoppinsInfo, loadFont as poppins } from "@remotion/google-fonts/Poppins";
import { getInfo as getAlfaSlabOneInfo, loadFont as alfaSlabOne } from "@remotion/google-fonts/AlfaSlabOne";
import { getInfo as getBangersInfo, loadFont as bangers } from "@remotion/google-fonts/Bangers";
import { getInfo as getBricolageGrotesqueInfo, loadFont as bricolageGrotesque } from "@remotion/google-fonts/BricolageGrotesque";
import { getInfo as getCalistogaInfo, loadFont as calistoga } from "@remotion/google-fonts/Calistoga";
import { getInfo as getCaprasimoInfo, loadFont as caprasimo } from "@remotion/google-fonts/Caprasimo";
import { getInfo as getDmMonoInfo, loadFont as dmMono } from "@remotion/google-fonts/DMMono";
import { getInfo as getDelaGothicOneInfo, loadFont as delaGothicOne } from "@remotion/google-fonts/DelaGothicOne";
import { getInfo as getInterInfo, loadFont as inter } from "@remotion/google-fonts/Inter";
import { getInfo as getLoraInfo, loadFont as lora } from "@remotion/google-fonts/Lora";
import { getInfo as getMerriweatherInfo, loadFont as merriweather } from "@remotion/google-fonts/Merriweather";
import { getInfo as getMochiyPopOneInfo, loadFont as mochiyPopOne } from "@remotion/google-fonts/MochiyPopOne";
import { getInfo as getPermanentMarkerInfo, loadFont as permanentMarker } from "@remotion/google-fonts/PermanentMarker";
import { getInfo as getStaatlichesInfo, loadFont as staatliches } from "@remotion/google-fonts/Staatliches";
import { getInfo as getSuezOneInfo, loadFont as suezOne } from "@remotion/google-fonts/SuezOne";
import { getInfo as getTitanOneInfo, loadFont as titanOne } from "@remotion/google-fonts/TitanOne";
import { getInfo as getClimateCrisisInfo, loadFont as climateCrisis } from "@remotion/google-fonts/ClimateCrisis";
import { getInfo as getInstrumentSerifInfo, loadFont as instrumentSerif } from "@remotion/google-fonts/InstrumentSerif";
import { getInfo as getOswaldInfo, loadFont as oswald } from "@remotion/google-fonts/Oswald";
import { getInfo as getIbmPlexMonoInfo, loadFont as ibmPlexMono } from "@remotion/google-fonts/IBMPlexMono";
import { getInfo as getAntonInfo, loadFont as anton } from "@remotion/google-fonts/Anton";
import { getInfo as getNunitoInfo, loadFont as nunito } from "@remotion/google-fonts/Nunito";
import { getInfo as getBebasNeueInfo, loadFont as bebasNeue } from "@remotion/google-fonts/BebasNeue";
import { getInfo as getRalewayInfo, loadFont as raleway } from "@remotion/google-fonts/Raleway";
import { getInfo as getQuicksandInfo, loadFont as quicksand } from "@remotion/google-fonts/Quicksand";
import { getInfo as getMulishInfo, loadFont as mulish } from "@remotion/google-fonts/Mulish";
import { getInfo as getSatisfyInfo, loadFont as satisfy } from "@remotion/google-fonts/Satisfy";
import { getInfo as getKalamInfo, loadFont as kalam } from "@remotion/google-fonts/Kalam";
import { getInfo as getSilkscreenInfo, loadFont as silkscreen } from "@remotion/google-fonts/Silkscreen";
import { getInfo as getRyeInfo, loadFont as rye } from "@remotion/google-fonts/Rye";
import { getInfo as getRubikWetPaintInfo, loadFont as RubikWetPaint } from "@remotion/google-fonts/RubikWetPaint";
import { getInfo as getBarriecitoInfo, loadFont as Barriecito } from "@remotion/google-fonts/Barriecito";
import { getInfo as getLuckiestGuyInfo, loadFont as LuckiestGuy } from "@remotion/google-fonts/LuckiestGuy";
import { getInfo as getBlackHanSansInfo, loadFont as BlackHanSans } from "@remotion/google-fonts/BlackHanSans";

// Custom fonts
import { loadFont } from "@remotion/fonts";

type FontLoadOptions = {
    text?: string | null;
};
type FontLoader = (options?: FontLoadOptions) => { fontFamily: string };
type GoogleFontLoader = (
    style?: string,
    options?: {
        weights?: string[];
        subsets?: string[];
        ignoreTooManyRequestsWarning?: boolean;
    }
) => { fontFamily: string };
type GoogleFontInfoResult = {
    fontFamily: string;
    unicodeRanges: Record<string, string>;
    fonts: Record<string, Record<string, Record<string, string>>>;
};
type GoogleFontInfo = () => GoogleFontInfoResult;
type SubsetMatcher = (subset: string, codePoint: number) => boolean;

const LATIN_SUBSET = "latin";
const REGULAR_WEIGHT = "400";
const NON_TEXT_SUBSETS = new Set(["math", "symbols"]);
const TEXT_SUBSET_PRIORITY = [
    "vietnamese",
    "latin-ext",
    "cyrillic",
    "cyrillic-ext",
    "greek",
    "greek-ext",
    "hebrew",
    "arabic",
    "devanagari",
    "bengali",
    "gurmukhi",
    "gujarati",
    "oriya",
    "tamil",
    "telugu",
    "kannada",
    "malayalam",
    "thai",
    "khmer",
    "myanmar",
    "korean",
    "japanese",
];

const parseUnicodeRange = (unicodeRange = ""): Array<[number, number]> => {
    const ranges: Array<[number, number]> = [];
    const rangePattern = /U\+([0-9A-Fa-f]+)(?:-([0-9A-Fa-f]+))?/g;
    let match = rangePattern.exec(unicodeRange);

    while (match) {
        const start = parseInt(match[1], 16);
        const end = match[2] ? parseInt(match[2], 16) : start;
        ranges.push([start, end]);
        match = rangePattern.exec(unicodeRange);
    }

    return ranges;
};

const getTextCodePoints = (text = "") => {
    const codePoints: number[] = [];

    for (let index = 0; index < text.length; index += 1) {
        const codePoint = text.codePointAt(index);

        if (codePoint === undefined) {
            continue;
        }

        codePoints.push(codePoint);

        if (codePoint > 0xffff) {
            index += 1;
        }
    }

    return codePoints;
};

const createSubsetMatcher = (unicodeRanges: Record<string, string>) => {
    const parsedRanges = new Map<string, Array<[number, number]>>();

    const getRangesForSubset = (subset: string) => {
        if (!parsedRanges.has(subset)) {
            parsedRanges.set(subset, parseUnicodeRange(unicodeRanges[subset]));
        }

        return parsedRanges.get(subset) || [];
    };

    return (subset: string, codePoint: number) => {
        return getRangesForSubset(subset).some(([start, end]) => codePoint >= start && codePoint <= end);
    };
};

const getPreferredSubset = (matchingSubsets: string[]) => {
    return TEXT_SUBSET_PRIORITY.find((subset) => matchingSubsets.includes(subset));
};

const getSubsetsForText = (
    availableSubsets: string[],
    subsetContainsCodePoint: SubsetMatcher,
    text?: string | null
) => {
    const subsets = new Set<string>();

    if (availableSubsets.includes(LATIN_SUBSET)) {
        subsets.add(LATIN_SUBSET);
    }

    if (!text) {
        return subsets.size > 0 ? Array.from(subsets) : availableSubsets;
    }

    const latinCoversCodePoint = (codePoint: number) => {
        return availableSubsets.includes(LATIN_SUBSET) && subsetContainsCodePoint(LATIN_SUBSET, codePoint);
    };

    getTextCodePoints(text).forEach((codePoint) => {
        if (latinCoversCodePoint(codePoint)) {
            return;
        }

        const matchingSubsets = availableSubsets.filter((subset) => subsetContainsCodePoint(subset, codePoint));
        const textSubsets = matchingSubsets.filter((subset) => !NON_TEXT_SUBSETS.has(subset));
        const preferredSubset = getPreferredSubset(textSubsets);

        if (preferredSubset) {
            subsets.add(preferredSubset);
            return;
        }

        (textSubsets.length ? textSubsets : matchingSubsets).forEach((subset) => subsets.add(subset));
    });

    return subsets.size > 0 ? Array.from(subsets) : availableSubsets;
};

const loadOnce = (load: FontLoader): FontLoader => {
    let result: { fontFamily: string } | null = null;

    return (options) => {
        if (!result) {
            result = load(options);
        }

        return result;
    };
};

const loadRegularGoogleFont = (fontLoader: unknown, getInfo: GoogleFontInfo): FontLoader => {
    const loadGoogleFont = fontLoader as GoogleFontLoader;
    const loadedSubsets = new Set<string>();
    let cachedInfo: {
        info: GoogleFontInfoResult;
        availableSubsets: string[];
        subsetContainsCodePoint: SubsetMatcher;
    } | null = null;

    const getCachedInfo = () => {
        if (!cachedInfo) {
            const info = getInfo();
            cachedInfo = {
                info,
                availableSubsets: Object.keys(info.fonts.normal[REGULAR_WEIGHT]),
                subsetContainsCodePoint: createSubsetMatcher(info.unicodeRanges || {}),
            };
        }

        return cachedInfo;
    };

    return (options) => {
        const { info, availableSubsets, subsetContainsCodePoint } = getCachedInfo();
        const subsets = getSubsetsForText(availableSubsets, subsetContainsCodePoint, options?.text);
        const subsetsToLoad = subsets.filter((subset) => !loadedSubsets.has(subset));

        if (subsetsToLoad.length > 0) {
            loadGoogleFont("normal", { weights: [REGULAR_WEIGHT], subsets: subsetsToLoad });
            subsetsToLoad.forEach((subset) => loadedSubsets.add(subset));
        }

        return { fontFamily: info.fontFamily };
    };
};

// Custom font loaders for Trebble fonts
const hellix = (): { fontFamily: string } => {
    loadFont({
        family: "Hellix",
        url: "https://web.trebble.fm/css/fonts/Hellix/Hellix-Regular.woff2",
        weight: "400",
    });

    // Load additional weights
    loadFont({
        family: "Hellix",
        url: "https://web.trebble.fm/css/fonts/Hellix/Hellix-SemiBold.woff2",
        weight: "600",
    });

    loadFont({
        family: "Hellix",
        url: "https://web.trebble.fm/css/fonts/Hellix/Hellix-ExtraBold.woff2",
        weight: "800",
    });

    return { fontFamily: "Hellix" };
};

const tiempos = (): { fontFamily: string } => {
    loadFont({
        family: "Tiempos",
        url: "https://web.trebble.fm/css/fonts/Tiempos/Text/tiempos-text-bold.woff2",
        weight: "700",
    });

    return { fontFamily: "Tiempos" };
};

// Single source of truth for font definitions
const FONT_DEFINITIONS: Record<string, { label: string; load: FontLoader }> = {
    // Custom Trebble fonts
    hellix: { label: 'Hellix', load: loadOnce(hellix) },
    tiempos: { label: 'Tiempos', load: loadOnce(tiempos) },

    // Google fonts
    tinos: { label: 'Tinos', load: loadRegularGoogleFont(tinos, getTinosInfo) },
    roboto: { label: 'Roboto', load: loadRegularGoogleFont(roboto, getRobotoInfo) },
    lato: { label: 'Lato', load: loadRegularGoogleFont(lato, getLatoInfo) },
    montserrat: { label: 'Montserrat', load: loadRegularGoogleFont(montserrat, getMontserratInfo) },
    openSans: { label: 'Open Sans', load: loadRegularGoogleFont(openSans, getOpenSansInfo) },
    playfairDisplay: { label: 'Playfair Display', load: loadRegularGoogleFont(playfairDisplay, getPlayfairDisplayInfo) },
    poppins: { label: 'Poppins', load: loadRegularGoogleFont(poppins, getPoppinsInfo) },
    alfaSlabOne: { label: 'Alfa Slab One', load: loadRegularGoogleFont(alfaSlabOne, getAlfaSlabOneInfo) },
    bangers: { label: 'Bangers', load: loadRegularGoogleFont(bangers, getBangersInfo) },
    bricolageGrotesque: { label: 'Bricolage Grotesque', load: loadRegularGoogleFont(bricolageGrotesque, getBricolageGrotesqueInfo) },
    calistoga: { label: 'Calistoga', load: loadRegularGoogleFont(calistoga, getCalistogaInfo) },
    caprasimo: { label: 'Caprasimo', load: loadRegularGoogleFont(caprasimo, getCaprasimoInfo) },
    dmMono: { label: 'DM Mono', load: loadRegularGoogleFont(dmMono, getDmMonoInfo) },
    delaGothicOne: { label: 'Dela Gothic One', load: loadRegularGoogleFont(delaGothicOne, getDelaGothicOneInfo) },
    inter: { label: 'Inter', load: loadRegularGoogleFont(inter, getInterInfo) },
    lora: { label: 'Lora', load: loadRegularGoogleFont(lora, getLoraInfo) },
    merriweather: { label: 'Merriweather', load: loadRegularGoogleFont(merriweather, getMerriweatherInfo) },
    mochiyPopOne: { label: 'Mochiy Pop One', load: loadRegularGoogleFont(mochiyPopOne, getMochiyPopOneInfo) },
    permanentMarker: { label: 'Permanent Marker', load: loadRegularGoogleFont(permanentMarker, getPermanentMarkerInfo) },
    staatliches: { label: 'Staatliches', load: loadRegularGoogleFont(staatliches, getStaatlichesInfo) },
    suezOne: { label: 'Suez One', load: loadRegularGoogleFont(suezOne, getSuezOneInfo) },
    titanOne: { label: 'Titan One', load: loadRegularGoogleFont(titanOne, getTitanOneInfo) },
    climateCrisis: { label: 'Climate Crisis', load: loadRegularGoogleFont(climateCrisis, getClimateCrisisInfo) },
    instrumentSerif: { label: 'Instrument Serif', load: loadRegularGoogleFont(instrumentSerif, getInstrumentSerifInfo) },
    oswald: { label: 'Oswald', load: loadRegularGoogleFont(oswald, getOswaldInfo) },
    ibmPlexMono: { label: 'IBM Plex Mono', load: loadRegularGoogleFont(ibmPlexMono, getIbmPlexMonoInfo) },
    anton: { label: 'Anton', load: loadRegularGoogleFont(anton, getAntonInfo) },
    nunito: { label: 'Nunito', load: loadRegularGoogleFont(nunito, getNunitoInfo) },
    bebasNeue: { label: 'Bebas Neue', load: loadRegularGoogleFont(bebasNeue, getBebasNeueInfo) },
    raleway: { label: 'Raleway', load: loadRegularGoogleFont(raleway, getRalewayInfo) },
    quicksand: { label: 'Quicksand', load: loadRegularGoogleFont(quicksand, getQuicksandInfo) },
    mulish: { label: 'Mulish', load: loadRegularGoogleFont(mulish, getMulishInfo) },
    satisfy: { label: 'Satisfy', load: loadRegularGoogleFont(satisfy, getSatisfyInfo) },
    kalam: { label: 'Kalam', load: loadRegularGoogleFont(kalam, getKalamInfo) },
    silkscreen: { label: 'Silkscreen', load: loadRegularGoogleFont(silkscreen, getSilkscreenInfo) },
    rye: { label: 'Rye', load: loadRegularGoogleFont(rye, getRyeInfo) },
    rubikWetPaint: { label: 'Rubik Wet Paint', load: loadRegularGoogleFont(RubikWetPaint, getRubikWetPaintInfo) },
    barriecito:{ label: 'Barriecito', load: loadRegularGoogleFont(Barriecito, getBarriecitoInfo) },
    luckiestGuy:{ label: 'Luckiest Guy', load: loadRegularGoogleFont(LuckiestGuy, getLuckiestGuyInfo) },
    blackHanSans:{ label: 'Black Han Sans', load: loadRegularGoogleFont(BlackHanSans, getBlackHanSansInfo) },

} as const;

// Export the font value type
export type FontValue = keyof typeof FONT_DEFINITIONS;
export type FontName = FontValue;

// Export font options for the select component
export const FONT_OPTIONS = Object.entries(FONT_DEFINITIONS).map(([value, { label }]) => ({
    value: value as FontValue,
    label,
    fontFamily: label
}));

// Export font names as constants
export const FONT_NAMES = Object.entries(FONT_DEFINITIONS).reduce((acc, [key, { label }]) => ({
    ...acc,
    [key.toUpperCase()]: { value: key, label }
}), {} as Record<string, { value: string; label: string }>);

// Load all fonts
export const loadAllFonts = () => {
    Object.keys(FONT_DEFINITIONS).forEach((fontValue) => {
        loadFontByValue(fontValue as FontValue);
    });
};

/**
 * Accepts strings because saved template and Timeloom configs can reach this layer
 * before their stored values are narrowed back to FontValue.
 */
export const loadFontByValue = (fontValue?: FontValue | string | null, options?: FontLoadOptions) => {
    const fontDefinition = fontValue ? FONT_DEFINITIONS[fontValue as FontValue] : null;
    if (!fontDefinition) {
        if (fontValue && process.env.NODE_ENV === 'development') {
            console.warn(`[loadFontByValue] Unknown font value: ${fontValue}`);
        }
        return "";
    }
    fontDefinition.load(options);
    return fontDefinition.label;
};

export const loadFontsByValue = (fontValues: Array<FontValue | string | null | undefined>, options?: FontLoadOptions) => {
    Array.from(new Set(fontValues.filter(Boolean) as Array<FontValue | string>)).forEach((fontValue) => {
        loadFontByValue(fontValue, options);
    });
};

// Create font map
export const FONT_MAP = Object.entries(FONT_DEFINITIONS).reduce((acc, [key, { label }]) => ({
    ...acc,
    [key]: label
}), {} as Record<FontValue, string>);
